import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { computed, action, makeObservable, observable, reaction } from "mobx";
import { AudioRecordingTransformer } from "@/store/audio/transforming/audio-recording-transformer";
import {
  AudioRecordingTransformingState,
  AudioRecordingTransformProgressTracker,
  IAudioTransformingStore,
  TransformCompletionResult,
  TransformMode,
  TransformPhase,
} from "@/store/audio/transforming/audio-transforming.types";
import { Uuid } from "@/domains/global/identifiers";
import type { IAudioTranscriptionStore } from "@/store/audio/transcription";

type AppStoreAudioTransformingStoreArgs = AppSubStoreArgs & {
  transcriptionStore: IAudioTranscriptionStore;
};

export class AppStoreAudioTransformingStore
  extends AppSubStore
  implements IAudioTransformingStore, AudioRecordingTransformProgressTracker
{
  private transformer: AudioRecordingTransformer;
  private transformingStateByRecording = new Map<Uuid, AudioRecordingTransformingState>();

  constructor(injectedDeps: AppStoreAudioTransformingStoreArgs) {
    super(injectedDeps);

    this.transformer = new AudioRecordingTransformer({
      store: injectedDeps.store,
      audioRecordings: injectedDeps.store.dexie.repositories.audioRecordings,
      transcriptionStore: injectedDeps.transcriptionStore,
      progressTracker: this,
    });

    makeObservable<
      AppStoreAudioTransformingStore,
      "transformer" | "transformingStateByRecording" | "toCompletionResult"
    >(this, {
      transformer: observable,
      transformingStateByRecording: observable.shallow,
      debugQueuedJobs: computed,
      enqueue: action,
      setTransformState: action,
      clearTransformState: action,
      resetState: action,
      getTransformState: false,
      waitForTransformComplete: false,
      toCompletionResult: false,
    });
  }

  get debugQueuedJobs() {
    return this.transformer.debugQueuedJobs;
  }

  async enqueue({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.transformer.enqueue({ audioRecordingId });
  }

  resetState() {
    this.transformingStateByRecording.clear();
    this.transformer.resetState();
  }

  setTransformState({
    audioRecordingId,
    state,
  }: {
    audioRecordingId: Uuid;
    state: AudioRecordingTransformingState;
  }): void {
    this.transformingStateByRecording.set(audioRecordingId, state);
  }

  clearTransformState({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.transformingStateByRecording.delete(audioRecordingId);
  }

  getTransformState({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingTransformingState {
    return (
      this.transformingStateByRecording.get(audioRecordingId) ?? {
        isTransforming: false,
        errorMessage: null,
        apiCallCompletedAt: null,
        phase: TransformPhase.Idle,
        mode: TransformMode.StandardFlow,
      }
    );
  }

  /**
   * Resolves once the transform for `audioRecordingId` reaches a terminal
   * state. Checks the current state first for an already-completed job, then
   * waits on a MobX reaction to the state map.
   */
  waitForTransformComplete({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<TransformCompletionResult> {
    const immediate = this.toCompletionResult(this.getTransformState({ audioRecordingId }));
    if (immediate) {
      return Promise.resolve(immediate);
    }

    return new Promise(resolve => {
      const dispose = reaction(
        () => this.toCompletionResult(this.getTransformState({ audioRecordingId })),
        result => {
          if (!result) return;
          dispose();
          resolve(result);
        },
        { fireImmediately: true }
      );
    });
  }

  private toCompletionResult(
    state: AudioRecordingTransformingState
  ): TransformCompletionResult | null {
    if (state.isTransforming) return null;
    if (state.errorMessage) return { success: false, error: state.errorMessage };
    if (state.apiCallCompletedAt !== null) return { success: true };
    return null;
  }
}
